import { useState, useRef, useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import Dropdown from 'rc-dropdown'
import SaveBackgroundButton from './SaveBackgroundButton'
import {
  addVirtualBackground,
  resetAddImage,
  setVirtualBackground,
} from '../vb/actions.js'
import {
  selectIsVbUploading,
  selectPendingVbImage,
  selectVbImage,
} from '../vb/reducer.js'
import { selectQuery } from './reducer.js'
import { setIsNotificationVisible } from './actions.js'
import { logResultImageClicked } from '../telemetry/telemetry.js'
import ellipsisIcon from './assets/ellipsis-icon.png'
import 'rc-dropdown/assets/index.css'
import './GeneratedImage.css'

const GenerateImage = ({ image, index, total }) => {
  const [isHover, setIsHover] = useState(false)
  const [isImageLoaded, setIsImageLoaded] = useState(false)
  const [isFocus, setIsFocus] = useState(false)
  const showEllipsisButton = (isHover && isImageLoaded) || isFocus
  const dispatch = useDispatch()
  const vbImage = useSelector(selectVbImage)
  const isVbUploading = useSelector(selectIsVbUploading)
  const pendingVbImage = useSelector(selectPendingVbImage)
  const isSelected = image.id === vbImage?.id
  const isPendingSelection =
    isVbUploading && image.id === pendingVbImage?.id
  const imageRef = useRef(null)
  const query = useSelector(selectQuery)

  useEffect(() => {
    if (isSelected && imageRef.current) {
      imageRef.current.focus()
      setIsFocus(true)
    }
  }, [isSelected])

  useEffect(() => {
    index === 0 && imageRef.current?.focus()
  }, [index])

  const handleMouseEnter = () => {
    setIsHover(true)
  }

  const handleMouseLeave = () => {
    setIsHover(false)
  }

  const handleSetVirtualBackground = (e) => {
    if (
      !isImageLoaded ||
      isVbUploading ||
      isSelected ||
      isPendingSelection
    ) {
      return
    }

    // prevent bubbled up event from its children
    if (imageRef.current.contains(e.target)) {
      dispatch(
        setVirtualBackground({
          image,
        })
      )
      logResultImageClicked()
    }
  }

  const handleSaveVirtualBackground = () => {
    dispatch(setIsNotificationVisible(false))
    dispatch(resetAddImage())
    dispatch(addVirtualBackground(image))
  }

  const handleLoad = () => {
    setIsImageLoaded(true)
  }

  const handleEllipsisClick = (e) => {
    // This will prevent the click event to propagate up to the root div of this component.
    e.stopPropagation()
  }

  const handleKeyDown = (e) => {
    if (
      e.key === 'ArrowUp' ||
      e.key === 'ArrowDown' ||
      e.key === 'ArrowLeft' ||
      e.key === 'ArrowRight'
    ) {
      e.preventDefault()
      // Move focus to the next/previous image based on arrow key pressed
      const direction = e.key === 'ArrowUp' || e.key === 'ArrowLeft' ? -1 : 1
      const images = document.querySelectorAll('.GeneratedImage-container')
      const currentIndex = Array.from(images).indexOf(imageRef.current)
      const newIndex = currentIndex + direction
      if (images[newIndex]) {
        images[newIndex]?.focus()
      }
    } else if (e.key === 'Enter') {
      e.preventDefault()
      handleSetVirtualBackground(e)
    }
  }

  const handleFocus = () => {
    setIsFocus(true)
  }

  const handleBlur = () => {
    setIsFocus(false)
  }

  return (
    <div
      ref={imageRef}
      className={`GeneratedImage-container ${
        !isImageLoaded ? 'disabled' : ''
      } ${isSelected ? 'selected' : ''} ${
        isVbUploading ? 'disabled' : ''
      } ${
        index + 1 === total ? 'lastGeneratedImage' : ''
      }`}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={handleSetVirtualBackground}
      onKeyDown={handleKeyDown}
      tabIndex={0}
      aria-disabled={!isImageLoaded || isVbUploading || isSelected}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      <img
        className="GeneratedImage"
        onLoad={handleLoad}
        src={image.src.dataUrl}
        alt={image.id}
        aria-label={`Results, grouping, ${query.trim()}, button, ${
          isSelected ? 'selected' : 'not selected'
        }, ${index + 1} of ${total}`}
      />

      {showEllipsisButton && (
        <Dropdown
          trigger={['click']}
          overlay={
            <SaveBackgroundButton
              setIsHover={setIsHover}
              handleSaveVirtualBackground={handleSaveVirtualBackground}
            />
          }
          placement="bottomRight"
        >
          <button
            className="GeneratedImage__ellipsis"
            onClick={handleEllipsisClick}
            aria-label="More options"
          >
            <img
              className="GeneratedImage__icon"
              src={ellipsisIcon}
              alt="ellipsisIcon"
              role="button"
            />
          </button>
        </Dropdown>
      )}
    </div>
  )
}

export default GenerateImage
